/* ==============================================================================
   Avatar
   ==============================================================================
   Cobre avatar de usuário, ícone de guild, ícone de papel e emoji custom —
   todos vêm da mesma tabela app.media e têm as mesmas regras de animação.

   A decisão que define este componente
   ------------------------------------
   GIF/WebP animado é suportado, mas NÃO anima por padrão. Motivo concreto e
   mensurável: durante uma chamada em mesh de 8 pessoas o cliente já mantém 7
   RTCPeerConnection e pode estar codificando até 14 fluxos de vídeo. Cada GIF
   animado na lista de membros é um decode contínuo competindo pela mesma CPU
   e GPU — e o sintoma que o usuário percebe é o compartilhamento de tela
   engasgando, sem ligação óbvia com os avatares.

   Por isso: poster estático por padrão, animação no hover, e a opção de
   "sempre animar" existe mas avisa o custo.

   O poster estático vem de media.variants (extraído do primeiro frame no
   upload). Nunca se carrega o GIF só para exibi-lo parado.
   ============================================================================== */

import { useCallback, useMemo, useState } from 'react';

/** Preferência do usuário, de app.user_settings.appearance.animateAvatars. */
export type AnimationPreference = 'always' | 'hover' | 'never';

export type AvatarStatus = 'online' | 'idle' | 'dnd' | 'offline';

/*
 * União fechada, e não `number`, de propósito: cada tamanho tem uma entrada
 * correspondente em STATUS_SIZE, e o compilador é que garante que nenhum
 * tamanho novo entre sem o indicador de status ser dimensionado junto. Um
 * `size: number` livre produziria um ponto de status de 6px num avatar de
 * 120px, e o defeito só apareceria na tela.
 *
 * 28 e 36 existem porque a barra superior tem 34px de altura de pastilha: 32
 * encosta na borda e 24 fica pequeno demais para o avatar do próprio usuário,
 * que é o único elemento de identidade permanente da interface.
 */
export type AvatarSize = 16 | 20 | 24 | 28 | 32 | 36 | 40 | 48 | 80 | 120;

export interface AvatarMedia {
  /** URL do poster estático (WebP). Sempre presente, inclusive em animados. */
  staticUrl: string;
  /** URL da versão animada. Ausente quando a mídia não é animada. */
  animatedUrl?: string;
  /** Placeholder de baixa resolução (thumbhash) — evita salto de layout. */
  placeholder?: string;
}

export interface AvatarProps {
  /** Ausente = usa o fallback com iniciais e gradiente. */
  media?: AvatarMedia | null;
  /**
   * Nome para as iniciais do fallback e para o alt.
   * Deve vir já resolvido: nickname da guild > display_name > username.
   */
  name: string;
  /** ID estável do usuário/guild. Semeia o gradiente do fallback. */
  seed: string;
  size?: AvatarSize;
  status?: AvatarStatus;
  /** Anel animado de "está falando". */
  speaking?: boolean;
  muted?: boolean;
  animationPreference?: AnimationPreference;
  /** Quadrado com canto arredondado, para ícone de guild. */
  shape?: 'circle' | 'squircle';
  className?: string;
  onClick?: () => void;
}

/* ------------------------------------------------------------------------------
   Fallback determinístico
   ------------------------------------------------------------------------------
   Mesmo usuário sempre recebe o mesmo gradiente, em qualquer dispositivo e
   sessão — o que faz o avatar funcionar como identificador visual.

   Nada de Math.random aqui: seria diferente a cada render e o usuário perderia
   a referência visual.
------------------------------------------------------------------------------ */
function hashSeed(seed: string): number {
  // FNV-1a de 32 bits: barato, boa distribuição, suficiente para escolher matiz.
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function fallbackGradient(seed: string): string {
  const hash = hashSeed(seed);
  // Dois matizes separados por 40-90 graus: sempre um gradiente com contraste
  // interno visível, nunca dois tons quase iguais.
  const hue = hash % 360;
  const shift = 40 + (hash % 50);
  // Croma e luminosidade fixos garantem que TODO fallback tenha contraste AA
  // com o texto branco das iniciais — independente do matiz sorteado.
  //
  // A luminosidade caiu de 58%/48% para 52%/42% porque a afirmação acima não se
  // sustentava: varrendo os 360 matizes, o pior caso (ciano-verde, ~190) dava
  // 3.65:1 com branco. O limite é o matiz, não a média — em OKLCH o mesmo L
  // rende luminância relativa diferente por matiz. Em 52% o pior caso passa a
  // 4.62:1, então TODO matiz sorteado atende AA.
  return `linear-gradient(135deg, oklch(52% 0.16 ${hue}) 0%, oklch(42% 0.17 ${
    (hue + shift) % 360
  }) 100%)`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** O indicador de status precisa crescer com o avatar para continuar legível. */
const STATUS_SIZE: Record<AvatarSize, number> = {
  16: 6, 20: 7, 24: 8, 28: 9, 32: 10, 36: 11, 40: 12, 48: 14, 80: 20, 120: 28,
};

const STATUS_COLOR: Record<AvatarStatus, string> = {
  online: 'var(--color-success)',
  idle: 'var(--color-warning)',
  dnd: 'var(--color-danger)',
  offline: 'var(--color-voice-idle)',
};

export function Avatar({
  media,
  name,
  seed,
  size = 40,
  status,
  speaking = false,
  muted = false,
  animationPreference = 'hover',
  shape = 'circle',
  className = '',
  onClick,
}: AvatarProps) {
  const [hovered, setHovered] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const isAnimated = Boolean(media?.animatedUrl);

  /**
   * Qual URL exibir.
   *
   * 'never'  -> sempre o poster, mesmo em hover. Respeita quem desligou por
   *             economia de bateria ou por sensibilidade a movimento.
   * 'hover'  -> poster em repouso, animado sob o cursor. Default.
   * 'always' -> animado direto.
   */
  const src = useMemo(() => {
    if (!media) return null;
    if (!isAnimated || animationPreference === 'never') return media.staticUrl;
    if (animationPreference === 'always') return media.animatedUrl!;
    return hovered ? media.animatedUrl! : media.staticUrl;
  }, [media, isAnimated, animationPreference, hovered]);

  const showFallback = !src || imageFailed;

  const radius = shape === 'circle' ? 'var(--radius-full)' : 'var(--radius-lg)';

  // Só rastreia hover quando isso muda algo. Em avatar estático, listener de
  // mouse em cada item de uma lista de 200 membros é desperdício puro.
  const tracksHover = isAnimated && animationPreference === 'hover';

  const handleEnter = useCallback(() => {
    if (tracksHover) setHovered(true);
  }, [tracksHover]);

  const handleLeave = useCallback(() => {
    if (tracksHover) setHovered(false);
  }, [tracksHover]);

  const interactive = Boolean(onClick);

  return (
    <div
      className={`relative shrink-0 ${className}`}
      style={{ width: size, height: size }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <div
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={onClick}
        onKeyDown={
          interactive
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick!();
                }
              }
            : undefined
        }
        className={[
          'relative grid h-full w-full place-items-center overflow-hidden',
          'transition-[box-shadow,transform]',
          speaking ? 'speaking-ring' : '',
          interactive ? 'cursor-pointer active:scale-95' : '',
        ].join(' ')}
        style={{
          borderRadius: radius,
          background: showFallback ? fallbackGradient(seed) : 'var(--color-surface-raised)',
          transitionDuration: 'var(--duration-fast)',
          transitionTimingFunction: 'var(--ease-out-quart)',
        }}
      >
        {showFallback ? (
          <span
            aria-hidden="true"
            className="select-none font-semibold leading-none text-white"
            style={{
              // Iniciais em ~38% da caixa: legível em 20px e proporcional em 120px.
              fontSize: Math.max(9, Math.round(size * 0.38)),
              // Sombra sutil garante leitura mesmo no gradiente mais claro sorteado.
              textShadow: 'var(--shadow-text)',
            }}
          >
            {initials(name)}
          </span>
        ) : (
          <img
            src={src!}
            alt={name}
            width={size}
            height={size}
            className="h-full w-full object-cover"
            /* Avatar fora da viewport não vale request. */
            loading="lazy"
            decoding="async"
            /* Falha de rede cai no gradiente, não em ícone de imagem quebrada. */
            onError={() => setImageFailed(true)}
            draggable={false}
          />
        )}
      </div>

      {/* ------------------------------------------------------------------------
          Indicador de status
          ------------------------------------------------------------------------
          Recortado do avatar com um anel da cor do fundo (e não desenhado por
          cima): assim o círculo de status fica nítido sobre qualquer avatar,
          inclusive um claro.

          O anel imitava --color-surface-base, mas avatar quase nunca fica sobre
          a base: fica em card, barra e lista, que são surface-raised. O
          resultado era um halo mais ESCURO que a superfície ao redor, visível
          como sujeira em volta do ponto de status. O padrão agora é raised, e um
          container em outra superfície corrige escrevendo --color-avatar-ring —
          sem precisar de prop nova.
      ------------------------------------------------------------------------ */}
      {status && (
        <span
          className="absolute rounded-full"
          style={{
            width: STATUS_SIZE[size],
            height: STATUS_SIZE[size],
            right: -1,
            bottom: -1,
            background: STATUS_COLOR[status],
            boxShadow: `0 0 0 ${Math.max(2, size / 20)}px var(--color-avatar-ring, var(--color-surface-raised))`,
          }}
          aria-hidden="true"
        />
      )}

      {/* Mudo: ícone além da cor — estado nunca é comunicado só por cor. */}
      {muted && (
        <span
          className="absolute grid place-items-center rounded-full"
          style={{
            width: STATUS_SIZE[size] + 4,
            height: STATUS_SIZE[size] + 4,
            right: -2,
            bottom: -2,
            background: 'var(--color-voice-muted)',
            boxShadow: `0 0 0 ${Math.max(2, size / 20)}px var(--color-avatar-ring, var(--color-surface-raised))`,
          }}
        >
          <svg
            width={Math.max(6, STATUS_SIZE[size] - 2)}
            height={Math.max(6, STATUS_SIZE[size] - 2)}
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M3 3l10 10"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M8 2.5a2 2 0 012 2v3.2L6 3.9A2 2 0 018 2.5z"
              fill="white"
            />
          </svg>
        </span>
      )}

      {/* Rótulo textual para leitor de tela: cor e anel não são anunciados. */}
      {(status || speaking || muted) && (
        <span className="sr-only">
          {name}
          {status ? `, ${status}` : ''}
          {speaking ? ', falando' : ''}
          {muted ? ', microfone desativado' : ''}
        </span>
      )}
    </div>
  );
}
